import React, { useState, useEffect, useCallback } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { GoogleLogo, FacebookLogo, Moon, Sun, Trash, CircleNotch, BellRinging } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import api from "@/lib/api";
import { isPushSubscribed, subscribeToPush, unsubscribeFromPush } from "@/lib/pushNotifications";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [accounts, setAccounts] = useState([]);
  const [onboardingState, setOnboardingState] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const fetchData = useCallback(async () => {
    const [accRes, obRes, statusRes] = await Promise.all([
      api.get("/integrations/accounts"),
      api.get("/onboarding/state"),
      api.get("/integrations/status"),
    ]);
    setAccounts(accRes.data.filter((a) => a.status !== "closed"));
    setOnboardingState(obRes.data);
    setIntegrationStatus(statusRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    isPushSubscribed().then(setPushEnabled);
  }, [fetchData]);

  const handleTogglePush = async (checked) => {
    setPushBusy(true);
    try {
      if (checked) {
        await subscribeToPush();
        toast.success("Push notifications enabled — you'll get alerts even when AdCrew OS is closed.");
      } else {
        await unsubscribeFromPush();
        toast.success("Push notifications disabled");
      }
      setPushEnabled(checked);
    } catch (e) {
      toast.error(e?.message || "Failed to update push notification settings");
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisconnect = async (accountId) => {
    try {
      await api.delete(`/integrations/accounts/${accountId}`);
      toast.success("Account disconnected");
      fetchData();
    } catch (e) {
      toast.error("Failed to disconnect account");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
          <div>
            <h1 className="font-heading font-semibold text-2xl tracking-tight">Account Settings</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your profile, connections, and preferences</p>
          </div>

          <Card>
            <CardHeader><CardTitle>Profile</CardTitle></CardHeader>
            <CardContent className="flex items-center gap-4">
              <Avatar className="h-14 w-14">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="text-lg">{user?.name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium" data-testid="settings-user-name">{user?.name}</p>
                <p className="text-sm text-muted-foreground" data-testid="settings-user-email">{user?.email}</p>
                <Badge variant="outline" className="mt-1 capitalize">{user?.tier === "none" ? "No plan yet" : `${user?.tier} tier`}</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Appearance</CardTitle></CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                <span>Dark mode</span>
              </div>
              <Switch checked={theme === "dark"} onCheckedChange={toggleTheme} data-testid="settings-dark-mode-switch" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Notifications</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <BellRinging className="h-4 w-4" />
                  <div>
                    <p>Push notifications</p>
                    <p className="text-xs text-muted-foreground">Get alerted about campaign changes even when AdCrew OS is closed.</p>
                  </div>
                </div>
                <Switch
                  checked={pushEnabled}
                  disabled={pushBusy}
                  onCheckedChange={handleTogglePush}
                  data-testid="settings-push-notifications-switch"
                />
              </div>
              <div className="flex items-center justify-between text-sm border-t border-border pt-3">
                <span className="text-muted-foreground">SMS alerts for critical events</span>
                <Badge variant={onboardingState?.sms_consent ? "success" : "outline"} data-testid="settings-sms-alert-status-badge">
                  {onboardingState?.sms_consent ? "Enabled" : "Not enabled"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Contact & Alerts</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Alert email</span>
                <span data-testid="settings-contact-email">{onboardingState?.contact_email || "Not connected"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Alert phone (SMS)</span>
                <span data-testid="settings-contact-phone">{onboardingState?.phone_number || "Not connected"}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Connected Ad Accounts</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <CircleNotch className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />
              ) : accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">No accounts connected yet.</p>
              ) : (
                accounts.map((acc) => (
                  <div key={acc.id} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`settings-account-row-${acc.id}`}>
                    <div className="flex items-center gap-3">
                      {acc.platform === "google" ? <GoogleLogo className="h-5 w-5" /> : <FacebookLogo className="h-5 w-5" />}
                      <div>
                        <p className="text-sm font-medium">{acc.account_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{acc.status} {acc.is_mock && "· Demo data"}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDisconnect(acc.id)} data-testid={`disconnect-account-${acc.id}`}>
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))
              )}
              <div className="pt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>Google Ads API:</span>
                <Badge variant={integrationStatus.google_ads_configured ? "success" : "outline"} data-testid="google-ads-api-status-badge">
                  {integrationStatus.google_ads_configured ? "Live credentials configured" : "Demo mode"}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>Meta Ads API:</span>
                <Badge variant={integrationStatus.meta_ads_configured ? "success" : "outline"} data-testid="meta-ads-api-status-badge">
                  {integrationStatus.meta_ads_configured ? "Live credentials configured" : "Demo mode"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
